// Standard Timekeeping (Planning/Timekeeping.md) — the opt-in bucketing
// layer sitting on top of ot-cost.ts's pure per-bucket math. Nothing here
// runs unless a rate-card role has useStandardTimekeeping set; budget.ts's
// costForBookingByDay branches to these functions only in that case, and
// the legacy per-day flat calculation is otherwise completely untouched.
import type { Call, RateCard } from './types.js';
import { standardHoursForCall, dateKey } from './quote.js';
import { costFromDayRateHoursStandard, costFromHourlyRateHoursStandard, type StandardCostOutput } from './ot-cost.js';

export type RateCardRole = RateCard['roles'][string];

// 'estimate' prices what an offer or quote is expected to cost from the
// SCHEDULE; 'actual' prices what was worked (timesheets, payroll). They differ
// for day rates only (Planning/Timekeeping.md): an estimate assumes the
// on-site coordinator provides the breaks and meals a 10-hour day needs and
// that rest between days gets sorted out, so a day of 10 hours or less is
// exactly the day rate with no short-rest penalty. Meals, breaks and short
// rest are accounted for — and penalized — in actual timekeeping.
export type CostMode = 'estimate' | 'actual';
export interface Holiday {
  date: Date;
  name: string;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// Compares calendar day (in the given zone), not raw instants — a holiday
// dated "2026-12-25" matches any call whose start falls on that calendar
// day in this org's own time zone, the same convention dateKey already
// establishes for OT/day-rate bucketing.
export function isHoliday(date: Date, holidays: Holiday[] | undefined, timeZone = 'UTC'): boolean {
  if (!holidays || holidays.length === 0) return false;
  const key = dateKey(date, timeZone);
  return holidays.some(h => dateKey(h.date, timeZone) === key);
}

interface DayBucket {
  key: string;
  calls: Call[];
  rawHours: number;
  isHoliday: boolean;
  firstCallStart: Date;
  lastCallEnd: Date;
}

// Groups calls into calendar-day buckets (chronological order), summing
// each day's standardHoursForCall (half-hour rounded) — the shared first
// step for both the hourly and day-rate standard-timekeeping pipelines.
function buildDayBuckets(calls: Call[], timeZone: string, holidays: Holiday[] | undefined): DayBucket[] {
  const sorted = [...calls].sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
  const byDay = new Map<string, Call[]>();
  const order: string[] = [];
  for (const call of sorted) {
    const key = dateKey(call.startTime, timeZone);
    const list = byDay.get(key);
    if (list) list.push(call);
    else {
      byDay.set(key, [call]);
      order.push(key);
    }
  }
  return order.map(key => {
    const dayCalls = byDay.get(key)!;
    return {
      key,
      calls: dayCalls,
      rawHours: dayCalls.reduce((sum, c) => sum + standardHoursForCall(c), 0),
      isHoliday: dayCalls.some(c => isHoliday(c.startTime, holidays, timeZone)),
      firstCallStart: dayCalls[0].startTime,
      lastCallEnd: dayCalls.reduce((max, c) => (c.endTime.getTime() > max.getTime() ? c.endTime : max), dayCalls[0].endTime),
    };
  });
}

// Merges chronologically-ordered day-buckets into continuous work
// stretches: a gap of at least restPeriodHrs between one bucket's last call
// and the next bucket's first call starts a new stretch — otherwise the
// buckets stay in the same stretch, letting OT accumulate across a
// calendar-day boundary (decision 4/6 in the timekeeping plan).
function groupIntoStretches<T extends Pick<DayBucket, 'firstCallStart' | 'lastCallEnd'>>(buckets: T[], restPeriodHrs: number): T[][] {
  const stretches: T[][] = [];
  let current: T[] = [];
  let prevEnd: Date | null = null;
  const restMs = restPeriodHrs * 60 * 60 * 1000;
  for (const bucket of buckets) {
    if (prevEnd && bucket.firstCallStart.getTime() - prevEnd.getTime() < restMs) {
      current.push(bucket);
    } else {
      if (current.length) stretches.push(current);
      current = [bucket];
    }
    prevEnd = bucket.lastCallEnd;
  }
  if (current.length) stretches.push(current);
  return stretches;
}

// Hourly pipeline: (1) day-bucket + floor each bucket's hours per decision
// 5 (first calendar day with hours -> firstCallMinHrs, every later one ->
// subsequentCallMinHrs — matches today's existing minHourlyBill precedent
// of a whole-day floor, not a per-Call-doc one); (2) merge buckets into
// rest-gap stretches; (3) a holiday bucket bills its own floored hours at
// flat OT, pulled out of its stretch's banding entirely; (4) band-cost the
// remaining stretch total, then allocate that cost back onto each
// contributing day-bucket proportional to its share of the stretch's
// hours (safe: costForBooking/buildLaborLines only ever read .totalCost
// off each Map entry, never the per-day band breakdown).
export function costForBookingByDayStandardHourly(
  calls: Call[],
  hourlyRate: number,
  roleRate: RateCardRole | undefined,
  holidays: Holiday[] | undefined,
  timeZone = 'UTC',
): Map<string, StandardCostOutput> {
  const result = new Map<string, StandardCostOutput>();
  if (calls.length === 0) return result;

  const ot1StartHrs = roleRate?.otStartHrs ?? 8;
  const ot2StartHrs = roleRate?.otSecondStartHrs ?? 12;
  const ot2Multiplier = roleRate?.otSecondMultiplier ?? 2.0;
  const restPeriodHrs = roleRate?.restPeriodHrs ?? 10;
  const firstCallMinHrs = roleRate?.firstCallMinHrs ?? 4;
  const subsequentCallMinHrs = roleRate?.subsequentCallMinHrs ?? 2;

  const dayBuckets = buildDayBuckets(calls, timeZone, holidays);
  const flooredBuckets = dayBuckets.map((b, i) => ({
    ...b,
    hours: Math.max(b.rawHours, i === 0 ? firstCallMinHrs : subsequentCallMinHrs),
  }));

  const stretches = groupIntoStretches(flooredBuckets, restPeriodHrs);

  for (const stretch of stretches) {
    const holidayBuckets = stretch.filter(b => b.isHoliday);
    const regularBuckets = stretch.filter(b => !b.isHoliday);

    for (const b of holidayBuckets) {
      result.set(b.key, costFromHourlyRateHoursStandard({ hoursWorked: b.hours, hourlyRate, isHoliday: true }));
    }

    if (regularBuckets.length > 0) {
      const stretchHours = regularBuckets.reduce((sum, b) => sum + b.hours, 0);
      const stretchCost = costFromHourlyRateHoursStandard({
        hoursWorked: stretchHours,
        hourlyRate,
        ot1StartHrs,
        ot2StartHrs,
        ot2Multiplier,
      });

      let remaining = stretchCost.totalCost;
      regularBuckets.forEach((b, idx) => {
        const isLast = idx === regularBuckets.length - 1;
        const share = stretchHours > 0 ? b.hours / stretchHours : 1 / regularBuckets.length;
        const totalCost = isLast ? round2(remaining) : round2(stretchCost.totalCost * share);
        remaining -= totalCost;
        result.set(b.key, {
          hoursWorked: b.hours,
          regularHours: stretchCost.regularHours * share,
          ot1Hours: stretchCost.ot1Hours * share,
          ot2Hours: stretchCost.ot2Hours * share,
          regularCost: round2(stretchCost.regularCost * share),
          ot1Cost: round2(stretchCost.ot1Cost * share),
          ot2Cost: round2(stretchCost.ot2Cost * share),
          totalCost,
          isHoliday: false,
          insufficientRest: false,
        });
      });
    }
  }

  return result;
}

// Day-rate pipeline: still one Map entry per calendar day (no allocation
// step needed) — band-cost each day-bucket's raw hours directly (no
// per-call minimum for day rate, that's an hourly-only concept), flagging
// insufficientRest when the gap since the previous bucket's last call is
// under restPeriodHrs, and isHoliday when any of that day's calls falls on
// a recognized holiday.
//
// In 'estimate' mode the short-rest penalty is not applied to a day whose
// hours are within the day-rate band (otStartHrs, default 10): that day is
// exactly the day rate. A day over the band keeps the penalty and the
// overtime, and the holiday premium is never suppressed (a holiday is known
// in advance). 'actual' (the default here) applies everything.
export function costForBookingByDayStandardDayRate(
  calls: Call[],
  dayRate: number,
  roleRate: RateCardRole | undefined,
  holidays: Holiday[] | undefined,
  timeZone = 'UTC',
  mode: CostMode = 'actual',
): Map<string, StandardCostOutput> {
  const result = new Map<string, StandardCostOutput>();
  if (calls.length === 0) return result;

  const ot1StartHrs = roleRate?.otStartHrs ?? 10;
  const ot2StartHrs = roleRate?.otSecondStartHrs ?? 12;
  const ot2Multiplier = roleRate?.otSecondMultiplier ?? 2.0;
  const restPeriodHrs = roleRate?.restPeriodHrs ?? 10;
  const restMs = restPeriodHrs * 60 * 60 * 1000;

  const buckets = buildDayBuckets(calls, timeZone, holidays);

  let prevEnd: Date | null = null;
  for (const bucket of buckets) {
    const shortRest = prevEnd != null && bucket.firstCallStart.getTime() - prevEnd.getTime() < restMs;
    const insufficientRest = shortRest && !(mode === 'estimate' && bucket.rawHours <= ot1StartHrs);
    result.set(
      bucket.key,
      costFromDayRateHoursStandard({
        hoursWorked: bucket.rawHours,
        dayRate,
        ot1StartHrs,
        ot2StartHrs,
        ot2Multiplier,
        insufficientRest,
        isHoliday: bucket.isHoliday,
      }),
    );
    prevEnd = bucket.lastCallEnd;
  }

  return result;
}
